import { useEffect, useState } from 'react';
import { SUITE, NOT_DEPLOYED, STEP_UP_WINDOW_MIN, type SuiteApp } from '../data/suite';
import { StepUpDialog } from './StepUp';
import { serverAssurance } from '../lib/mfa';
import { BACKEND } from '../lib/db';
import './suite.css';

/* =====================================================================================
   Suite launcher.

   Every Ariadne app is one click from anywhere in Forum.

   Tier 'sso' opens directly with no sign-in at all, because you are already signed in to
   Forum and the session is shared.

   Tier 'step_up' requires a fresh second factor first: a passkey touch, or a TOTP code
   where the device has no authenticator. This is NOT a password retype. It is a real
   cryptographic re-authentication that takes about a second, it cannot be phished into a
   fake dialog because the browser owns the passkey prompt, and it stays valid for
   STEP_UP_WINDOW_MIN minutes so opening Locker twice in a row does not ask twice.

   Crucially the same freshness is enforced in the database (locker_stepup_fresh() in
   db/locker_notebook_rls.sql), so this is a real control rather than a screen that can be
   navigated around.
   ===================================================================================== */

function AppIcon({ name }: { name: string }) {
  const paths: Record<string, JSX.Element> = {
    thread: <><path d="M4 18c4-10 12-10 16 0" /><circle cx="4" cy="18" r="2" /><circle cx="20" cy="18" r="2" /></>,
    library: <><path d="M4 5h5v14H4zM11 5h4v14h-4z" /><path d="M17 6l3 13" /></>,
    sculptor: <><path d="M5 19l6-6" /><path d="M12 12l4-4 3 3-4 4z" /><path d="M14 6l4 4" /></>,
    compass: <><circle cx="12" cy="12" r="9" /><path d="M15.5 8.5l-2 5-5 2 2-5z" /></>,
    notebook: <><path d="M6 4h11a2 2 0 0 1 2 2v14H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><path d="M9 4v16" /></>,
    locker: <><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
  };
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name] ?? paths.compass}
    </svg>
  );
}

/* A step-up remains valid for a short window so repeated access does not nag. Held in
   memory only: closing the tab discards it, which is the correct behaviour for a control
   whose purpose is defeating an unattended laptop. */
let stepUpUntil: Record<string, number> = {};

/* Which apps demand a step-up is TENANT DATA, not shared code. app.suite_access (migration
   0027) carries requires_step_up and max_age_minutes per app, in each tenant's own database,
   so a white-label tenant turns the gate off with one UPDATE and no deployment. The registry
   tier in suite.ts is only the fallback: it applies while the rows are loading, where there
   is no backend at all (demo builds), and where a row is missing, and it falls back STRICT,
   which is the right direction for a security control. */
interface AccessRow { requires_step_up: boolean; max_age_minutes: number | null }
let accessCache: Record<string, AccessRow> | null = null;

async function loadSuiteAccess(): Promise<Record<string, AccessRow>> {
  if (BACKEND !== 'supabase') return {};
  if (accessCache) return accessCache;
  try {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase()
      .schema('app').from('suite_access')
      .select('key, requires_step_up, max_age_minutes');
    if (error || !data) return {};
    accessCache = Object.fromEntries(
      (data as ({ key: string } & AccessRow)[]).map((r) => [r.key, r]),
    );
    return accessCache;
  } catch {
    return {};
  }
}

export function SuiteLauncher({ onClose }: { onClose: () => void }) {
  const [pendingApp, setPendingApp] = useState<SuiteApp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [access, setAccess] = useState<Record<string, AccessRow>>(accessCache ?? {});

  useEffect(() => {
    let live = true;
    loadSuiteAccess().then((a) => { if (live) setAccess(a); });
    return () => { live = false; };
  }, []);

  function requiresStepUp(app: SuiteApp) {
    const row = access[app.key];
    return row ? row.requires_step_up : app.tier === 'step_up';
  }

  function windowMinutes(app: SuiteApp) {
    return access[app.key]?.max_age_minutes ?? STEP_UP_WINDOW_MIN;
  }

  function stillFresh(app: SuiteApp) {
    return (stepUpUntil[app.key] ?? 0) > Date.now();
  }

  function open(app: SuiteApp) {
    window.open(app.url, '_blank', 'noopener,noreferrer');
    onClose();
  }

  function launch(app: SuiteApp) {
    setError(null);
    // Apps the tenant leaves ungated open straight away. A gated app opens only if a second
    // factor was presented recently enough; otherwise it goes to the dialog.
    if (!requiresStepUp(app) || stillFresh(app)) return open(app);
    setPendingApp(app);
  }

  /* The old implementation ran navigator.credentials.get() and, if the promise resolved,
     opened the application. It could never succeed, because nothing enrolled a passkey — and
     had it succeeded it would have proved nothing, since the server never saw it.

     Now the dialog performs a real Supabase TOTP challenge. Supabase reissues the session
     token with aal2 on success, which is what app.step_up_fresh() reads, so the evidence is
     verifiable by the database rather than asserted by this page. */
  async function onVerified() {
    const app = pendingApp;
    setPendingApp(null);
    if (!app) return;

    // Ask the SERVER what the session now holds. Trusting the client's own success callback
    // would reintroduce exactly the flaw this replaced.
    const a = await serverAssurance();
    if (a && !a.steppedUp) {
      // The server did not record the factor. Do not open, and say why.
      setError('Verified, but the server did not record a fresh second factor. '
             + app.name + ' was not opened.');
      return;
    }
    stepUpUntil[app.key] = Date.now() + windowMinutes(app) * 60_000;
    open(app);
  }

  const sso = SUITE.filter((a) => !requiresStepUp(a));
  const restricted = SUITE.filter((a) => requiresStepUp(a));

  return (
    <>
      <div className="suite-scrim" onClick={onClose} />
      <div className="suite card" role="dialog" aria-label="Ariadne Suite">
        <>
            <header className="suite-head">
              <div>
                <div className="label">Ariadne Suite</div>
                <div className="suite-title display">Open another application</div>
              </div>
              <button className="icon-btn" onClick={onClose} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.8" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </header>

            {sso.length > 0 && <section className="suite-group">
              <div className="suite-group-label">One click, no sign-in</div>
              <div className="suite-grid">
                {sso.map((a) => (
                  <button key={a.key} className="suite-app" onClick={() => launch(a)}>
                    <span className="suite-icon"><AppIcon name={a.icon} /></span>
                    <span className="suite-app-main">
                      <span className="suite-app-name">{a.name}</span>
                      <span className="suite-app-tag">{a.tagline}</span>
                    </span>
                    <span className="suite-arrow">↗</span>
                  </button>
                ))}
              </div>
            </section>}

            {restricted.length > 0 && <section className="suite-group">
              <div className="suite-group-label restricted">A six-digit code to confirm it is you</div>
              <div className="suite-grid">
                {restricted.map((a) => (
                  <button key={a.key} className="suite-app restricted" onClick={() => launch(a)}>
                    <span className="suite-icon"><AppIcon name={a.icon} /></span>
                    <span className="suite-app-main">
                      <span className="suite-app-name">
                        {a.name}{' '}
                        {stillFresh(a)
                          ? <span className="pill ok">confirmed</span>
                          : <span className="pill warn">code needed</span>}
                      </span>
                      <span className="suite-app-tag">{a.tagline}</span>
                    </span>
                    <span className="suite-arrow">{stillFresh(a) ? '↗' : '🔒'}</span>
                  </button>
                ))}
              </div>
            </section>}

            {error && <p className="suite-error" role="alert">{error}</p>}

            <footer className="suite-foot">
              Forum is a Patient Thread product. {NOT_DEPLOYED.join(', ')} has no deployed
              application and is not listed.
            </footer>
        </>
      </div>
      {pendingApp && (
        <StepUpDialog
          appName={pendingApp.name}
          reason={pendingApp.sensitivity ?? null}
          onVerified={onVerified}
          onClose={() => setPendingApp(null)}
        />
      )}
    </>
  );
}
